import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp, TrendingDown, Minus, Flame, Zap, Thermometer, Eye,
  ChevronDown, ChevronUp, BarChart3, Lock, ExternalLink, Users,
} from 'lucide-react';
import openExternal from '@/utils/openExternal';
import { analystUrlFor } from '@/utils/analystLinks';
import { logoUrlFor, colorForName, symbolForName } from '@/utils/companyLogo';
import AnalystConsensusPill from './AnalystConsensusPill';
import SignalLensFrame from './SignalLensFrame';

const ACTION_STYLES = {
  strong_buy: { border: '#10B981', text: '#10B981' },
  buy:        { border: '#34D399', text: '#34D399' },
  hold:       { border: '#EAB308', text: '#FACC15' },
  sell:       { border: '#EF4444', text: '#F87171' },
  strong_sell:{ border: '#EF4444', text: '#EF4444' },
};

// Labels vary by tab context
const ACTION_LABELS = {
  investor: {
    strong_buy: 'STRONG BULLISH', buy: 'BULLISH', hold: 'NEUTRAL', sell: 'BEARISH', strong_sell: 'STRONG BEARISH',
  },
  reseller: {
    strong_buy: 'VERY HOT', buy: 'HOT', hold: 'STEADY', sell: 'COOLING', strong_sell: 'COLD',
  },
  tiktok: {
    strong_buy: 'VERY HOT', buy: 'HOT', hold: 'STEADY', sell: 'COOLING', strong_sell: 'COLD',
  },
};

const LEVEL_CONFIG = {
  on_fire:    { Icon: Flame, color: '#FF3B30', label: 'High Buzz' },
  heating_up: { Icon: Zap, color: '#FF9500', label: 'Rising Buzz' },
  warming:    { Icon: Thermometer, color: '#FFCC00', label: 'Some Buzz' },
  watching:   { Icon: Eye, color: '#8E8E93', label: 'Quiet' },
};

const DIRECTION_MAP = {
  strongly_bullish: { icon: '↑↑', color: '#10B981' },
  bullish:          { icon: '↑', color: '#34D399' },
  neutral:          { icon: '→', color: '#A1A1AA' },
  bearish:          { icon: '↓', color: '#F87171' },
  strongly_bearish: { icon: '↓↓', color: '#EF4444' },
  mixed:            { icon: '↕', color: '#EAB308' },
};

const ScoreBar = ({ label, value, max = 30, hint }) => {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div data-testid={`score-bar-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-gray-500 w-14 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{label}</span>
        <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }}
            className="h-full rounded-full" style={{ background: pct > 60 ? '#10B981' : pct > 30 ? '#EAB308' : '#8E8E93' }} />
        </div>
        <span className="text-[10px] text-gray-400 w-9 text-right tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {Math.round(value)}<span className="text-gray-600">/{max}</span>
        </span>
      </div>
      {hint && (
        <p className="text-[9px] text-gray-500 ml-16 mt-0.5 leading-snug" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {hint}
        </p>
      )}
    </div>
  );
};

export default function TrendCard({ entity, persona, index = 0, blurred = false, isNews = false, tabContext = 'investor', onSubscribe, borderColor = null, onClick = null }) {
  const [expanded, setExpanded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const bs = entity.buy_sell?.[persona] || entity.buy_sell?.investor || {};
  const action = bs.action || 'hold';
  const actionStyle = ACTION_STYLES[action] || ACTION_STYLES.hold;
  const actionLabel = (ACTION_LABELS[tabContext] || ACTION_LABELS.investor)[action] || action.toUpperCase();
  const level = LEVEL_CONFIG[entity.trend_level] || LEVEL_CONFIG.watching;
  const dir = DIRECTION_MAP[entity.direction_consensus] || DIRECTION_MAP.neutral;
  const breakdown = entity.score_breakdown || {};
  const insight = persona === 'reseller' ? entity.insight_reseller : entity.insight_investor;

  // Source link for news articles
  const sourceUrl = entity.source_url || entity.article_url || entity.raw_data?.link || entity.raw_data?.url || null;

  // Image — show on any card type when source data carries one (iter 61e).
  // Falls back through several common field names so investor/reseller cards
  // can pick up logos/product images once backend enriches them.
  const rawImage = entity.image_url || entity.logo_url || entity.thumbnail
                 || entity.product_image || entity.image || '';

  // Logo fallback (May 2026): when no source image, try a Clearbit company
  // logo derived from the entity name/ticker. If that also fails (Clearbit
  // returns 404 → onError triggers), we render an initial-avatar tile so the
  // card never has an empty grey square.
  const logoFallback = !rawImage && !isNews
    ? logoUrlFor({ name: entity.entity_name, ticker: entity.asset || entity.ticker || entity.symbol })
    : null;
  const imageUrl = rawImage || logoFallback || '';
  const showImage = !!imageUrl && !imageFailed;
  // News cards always render the right-side thumbnail block (with image or
  // fallback); investor/reseller render the logo+initial-avatar fallback so
  // every card has visual identity per user request May 2026.
  const showInitialAvatar = !showImage && !isNews;

  // Determine whether the Details section would actually have any content.
  // News cards never have insight/breakdown/why-trending and always show their
  // source link inline at the top — so the Details expand is empty for news.
  // Hide it entirely on news cards (per user request, iter 61d).
  const hasBreakdown = !isNews && entity.score_breakdown && Object.keys(entity.score_breakdown).length > 0;
  const hasInsight = !isNews && !!insight;
  const hasWhyTrending = !isNews && !!entity.why_trending;
  const hasDetails = !isNews && !!(sourceUrl || hasInsight || hasWhyTrending || hasBreakdown);

  if (blurred) {
    return (
      <motion.div
        data-testid={`trend-card-blurred-${index}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.03 }}
        className="relative rounded-xl overflow-hidden"
        style={{ background: '#121212', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="p-3 filter blur-[6px] pointer-events-none select-none">
          <div className="flex items-center justify-between">
            <span className="text-white text-sm font-bold truncate">{entity.entity_name}</span>
            <span className="text-sm font-black" style={{ fontFamily: 'JetBrains Mono', color: level.color }}>{Math.round(entity.hotness_score)}</span>
          </div>
          <p className="text-gray-400 text-xs mt-1 line-clamp-1">{entity.headline}</p>
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm">
          <Lock size={18} className="text-white/60 mb-2" />
          <button
            data-testid={`subscribe-gate-${index}`}
            onClick={onSubscribe}
            className="px-5 py-2 rounded-full text-xs font-bold text-white"
            style={{ background: 'linear-gradient(to right, #F97316, #EC4899, #8B5CF6)' }}
          >
            Subscribe to unlock
          </button>
        </div>
      </motion.div>
    );
  }

  const textColor = undefined;
  const mutedColor = undefined;

  return (
    <motion.div
      data-testid={`trend-card-${entity.canonical_id}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      onClick={onClick ? () => onClick(entity) : undefined}
      className={`rounded-xl overflow-hidden ${onClick ? 'cursor-pointer hover:brightness-110 active:scale-[0.99] transition-all' : ''}`}
      style={{
        background: '#121212',
        // May 2026 user spec: every card carries the active tab's colour as a
        // bold 2-px outline (News=blue, Investor=yellow, Reseller=cyan).
        // Falls back to action-tinted border on the rare blurred-card path.
        border: borderColor
          ? `2px solid ${borderColor}`
          : `1px solid ${isNews ? 'rgba(255,255,255,0.12)' : actionStyle.border + '30'}`,
        boxShadow: borderColor ? `0 0 0 1px ${borderColor}25 inset` : 'none',
      }}
    >
      {/* Top row: text on the left, square thumbnail on the right (iter 61i).
          Card is sized just 50% taller than the original — image is a fixed
          200×200 square on the right; the rest of the card height is driven
          by the text content + image and stays sensible. */}
      <div className="flex gap-2">
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Header: Name + Score */}
          <div className="p-4 pb-2">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 data-testid={`trend-name-${entity.canonical_id}`}
            className="font-black text-[17px] leading-tight truncate flex-1"
            style={{ fontFamily: 'Manrope, sans-serif', color: '#fff', letterSpacing: '-0.01em' }}>
            {entity.entity_name}
          </h3>
          <span className="flex items-center gap-1 flex-shrink-0">
            {/* Top-right hotness pill: kept for non-investor (reseller/news)
                cards. Investor cards moved to the dual-lens SignalLensFrame
                below where Current Trend score is shown clearly per
                user spec May 2026 ("only 2 numbers, remove hotness"). */}
            {!isNews && tabContext !== 'investor' && (
              <span data-testid={`trend-score-${entity.canonical_id}`}
                className="text-2xl font-black leading-none flex items-center gap-1"
                style={{ fontFamily: 'JetBrains Mono, monospace', color: level.color }}>
                <span className="text-sm font-bold" style={{ color: dir.color }}>{dir.icon}</span>
                {Math.round(entity.hotness_score)}
              </span>
            )}
          </span>
        </div>

        {/* Inline badges */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Trend level badge — only for reseller/tiktok/news, not investor */}
          {tabContext !== 'investor' && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
              style={{ background: `${level.color}18`, color: level.color }}>
              <level.Icon size={10} /> {level.label}
            </span>
          )}
          <span className="text-[11px] font-black uppercase tracking-wider px-2 py-0.5 rounded"
            style={{ color: '#F3F4F6', background: 'rgba(255,255,255,0.10)' }}>
            {entity.category}
          </span>
          {!isNews && tabContext !== 'investor' && (
            <span data-testid={`trend-action-${entity.canonical_id}`}
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
              style={{ color: actionStyle.text, background: `${actionStyle.border}18` }}>
              {action.includes('buy') ? '↑' : action.includes('sell') ? '↓' : '–'} {actionLabel}
            </span>
          )}
          {persona === 'reseller' && entity.platform && (
            <span data-testid={`trend-platform-${entity.canonical_id}`}
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
              style={{ color: '#A855F7', background: 'rgba(168,85,247,0.12)' }}>
              {entity.platform}
            </span>
          )}
        </div>
      </div>

      {/* Headline */}
      {entity.headline && (
        <div className="px-4 pb-2">
          <p className="text-[14px] font-semibold leading-relaxed line-clamp-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#E5E7EB' }}>
            {entity.headline}
          </p>
        </div>
      )}

      {/* AI hook — pre-generated explanation. */}
      {isNews && entity.card_hook && entity.card_hook !== entity.headline && (
        <div className="px-4 pb-2">
          <p className="text-[13px] font-medium leading-relaxed line-clamp-3" style={{ fontFamily: 'Manrope, sans-serif', color: '#F3F4F6' }}>
            {entity.card_hook}
          </p>
        </div>
      )}

      {/* Source label for news */}
      {isNews && entity.why_trending && (
        <div className="px-4 pb-2">
          <span className="text-[12px] font-semibold italic" style={{ color: '#D1D5DB' }}>{entity.why_trending}</span>
        </div>
      )}
        </div>

        {/* Right-side SQUARE thumbnail — responsive (May 2026 iter 56):
            96×96 on small phones, scales up to 128×128 on roomier widths.
            Was a fixed 200×200 which dominated mobile cards. */}
        {showImage && (
          <div
            data-testid={`trend-image-${entity.canonical_id}`}
            className="shrink-0 self-start mt-3 mr-3 rounded-lg overflow-hidden w-20 h-20 sm:w-24 sm:h-24"
            style={{
              background: 'linear-gradient(135deg,#1A1A1A,#0F0F0F)',
            }}
          >
            <img
              src={imageUrl}
              alt=""
              loading="lazy"
              onError={() => setImageFailed(true)}
              className="w-full h-full object-cover"
              style={{ display: 'block' }}
            />
          </div>
        )}
        {/* Fallback initial-avatar — same responsive size as the image tile so
            cards without a logo still have visual identity but don't dominate
            the mobile card height (May 2026 user spec). */}
        {showInitialAvatar && (() => {
          // Prefer a currency / crypto glyph (£/$/€/₿) when the entity is a
          // known forex/crypto ticker — much more recognisable than a bare
          // first letter ("G" for "GBP/USD"). Falls back to first letter for
          // ordinary brand names.
          const glyph = symbolForName(entity.entity_name || '');
          const initial = glyph || (entity.entity_name || '?').trim().charAt(0).toUpperCase();
          const accent = colorForName(entity.entity_name || '');
          return (
            <div
              data-testid={`trend-initial-${entity.canonical_id}`}
              className="shrink-0 self-start mt-3 mr-3 rounded-lg overflow-hidden flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24"
              style={{
                background: `linear-gradient(135deg, ${accent}30, ${accent}10), #0F0F0F`,
                border: `1px solid ${accent}40`,
              }}
            >
              <span
                className={`font-black select-none ${glyph && glyph.length > 1 ? 'text-3xl sm:text-4xl' : 'text-4xl sm:text-5xl'}`}
                style={{
                  fontFamily: 'Unbounded, sans-serif',
                  lineHeight: 1,
                  color: accent,
                  textShadow: `0 2px 24px ${accent}55`,
                }}
              >
                {initial}
              </span>
            </div>
          );
        })()}
      </div>

      {/* Dual-lens frame — Investor + Reseller share the same comparative
          layout (Trend Score left + Projection right). Persona just swaps the
          labels and which API to call for breakdown details (May 2026). */}
      {!isNews && (tabContext === 'investor' || tabContext === 'reseller') && (
        <SignalLensFrame entity={entity} persona={tabContext} />
      )}

      {/* Top-level Source article link — always visible when we have one.
          For news cards we keep the existing Details-nested link to avoid duplication. */}
      {!isNews && sourceUrl && (
        <div className="px-3 pb-2 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid={`trend-source-link-top-${entity.canonical_id}`}
            onClick={(e) => {
              e.stopPropagation();
              openExternal(sourceUrl);
            }}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold transition-colors"
            style={{ color: '#60A5FA' }}>
            <ExternalLink size={11} /> Source article
          </button>
          {/* Analyst report link (FB-COV-2/3) — surfaces TipRanks/Zacks/Moneycontrol/CoinGecko per asset type. */}
          {(() => {
            const analystUrl = analystUrlFor({
              symbol: entity.asset || entity.ticker || entity.symbol,
              asset_type: entity.asset_type,
              region: entity.region,
            });
            if (!analystUrl) return null;
            return (
              <button
                type="button"
                data-testid={`trend-analyst-link-${entity.canonical_id}`}
                onClick={(e) => { e.stopPropagation(); openExternal(analystUrl); }}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold transition-colors"
                style={{ color: '#A78BFA' }}>
                <ExternalLink size={11} /> Analyst forecast
              </button>
            );
          })()}
        </div>
      )}

      {/* Expand toggle — only when there's actually something in Details */}
      {hasDetails && (
        <div className="px-3 pb-2">
          <button data-testid={`trend-expand-${entity.canonical_id}`}
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="flex items-center gap-1 text-[10px] transition-colors"
            style={{ color: '#6B7280' }}>
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? 'Less' : 'Details'}
          </button>
        </div>
      )}

      <AnimatePresence>
        {expanded && hasDetails && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">

            {/* Source link — opens original article in a new window/tab */}
            {sourceUrl && (
              <div className="px-3 pb-2">
                <button
                  type="button"
                  data-testid={`trend-source-link-${entity.canonical_id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    openExternal(sourceUrl);
                  }}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold transition-colors"
                  style={{ color: '#60A5FA' }}>
                  <ExternalLink size={11} /> Source article
                </button>
              </div>
            )}

            {/* AI Insight (premium only) */}
            {!isNews && insight && (
              <div className="mx-3 mb-2 rounded-lg p-2" style={{
                background: 'rgba(26,26,26,0.5)',
                borderLeft: `2px solid ${actionStyle.border}`,
                border: '1px solid rgba(255,255,255,0.05)',
              }}>
                <p className="text-[11px] italic leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif', color: '#D1D5DB' }}>"{insight}"</p>
              </div>
            )}

            {/* Why trending */}
            {!isNews && entity.why_trending && (
              <div className="px-3 pb-2">
                <p className="text-[10px]" style={{ color: mutedColor || '#6B7280' }}>
                  <span className="font-semibold" style={{ color: '#9CA3AF' }}>Why: </span>{entity.why_trending}
                </p>
              </div>
            )}

            {/* Score Breakdown (premium only) */}
            {!isNews && breakdown && Object.keys(breakdown).length > 0 && (
              <div className="mx-3 mb-2 rounded-lg p-2.5" style={{ background: '#1A1A1A' }} data-testid="score-breakdown">
                <div className="flex items-center gap-1 mb-1">
                  <BarChart3 size={10} style={{ color: mutedColor || '#6B7280' }} />
                  <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: mutedColor || '#6B7280' }}>How we got to {Math.round(entity.hotness_score || 0)}/100</span>
                </div>
                <p className="text-[9.5px] leading-snug mb-2" style={{ color: '#9CA3AF', fontFamily: 'Manrope, sans-serif' }}>
                  Five components add up to the hotness score. Each bar shows this entity's value out of its max.
                </p>
                <div className="space-y-2">
                  <ScoreBar
                    label="Data"
                    value={breakdown.structured_strength || 0}
                    max={30}
                    hint="Price/volume strength from APIs (z-scores + magnitudes)"
                  />
                  <ScoreBar
                    label="Editorial"
                    value={breakdown.editorial_velocity || 0}
                    max={25}
                    hint="How loud the news cycle is around this entity"
                  />
                  <ScoreBar
                    label="Sources"
                    value={breakdown.source_diversity || 0}
                    max={20}
                    hint="How many independent sources mention it (more = stronger)"
                  />
                  <ScoreBar
                    label="Signals"
                    value={breakdown.signal_type_diversity || 0}
                    max={15}
                    hint="Variety of signal types (price + earnings + ratings + news…)"
                  />
                  <ScoreBar
                    label="Cross"
                    value={breakdown.cross_type_bonus || 0}
                    max={10}
                    hint="Bonus when both data and editorial confirm the same direction"
                  />
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
